import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Fade,
  Grow,
  Grid,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchCourse } from '../store/thunks/coursesThunks';
import SchoolIcon from '@mui/icons-material/School';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import QuizIcon from '@mui/icons-material/Quiz';
import YouTubeIcon from '@mui/icons-material/YouTube';
import BookIcon from '@mui/icons-material/Book';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MarkdownRenderer from '../components/MarkdownRenderer';
import AdSenseBanner from '../components/AdSenseBanner';
import CourseRating from '../components/courses/CourseRating';
import CourseComments from '../components/courses/CourseComments';
import { trackCourseViewed } from '../services/analytics/firebaseAnalytics';
import SEO from '../components/SEO';

const CourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { selectedCourse, loading, error } = useAppSelector((state) => state.courses);

  useEffect(() => {
    if (courseId && (!selectedCourse || selectedCourse.id !== courseId)) {
      dispatch(fetchCourse(courseId));
    }
  }, [courseId, dispatch, selectedCourse]);

  useEffect(() => {
    if (selectedCourse) {
      trackCourseViewed(selectedCourse.id, selectedCourse.title);
    }
  }, [selectedCourse]);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60vh',
            gap: 3,
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
          <Typography variant="h6" color="text.secondary">
            Loading course...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !selectedCourse) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert
            severity="error"
            sx={{
              borderRadius: 3,
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
            }}
          >
            {error || 'Course not found'}
          </Alert>
          <Button
            onClick={() => navigate('/courses')}
            sx={{
              mt: 2,
              borderRadius: 2,
            }}
          >
            Back to Courses
          </Button>
        </Box>
      </Container>
    );
  }

  const handleStartCourse = () => {
    navigate(`/courses/${courseId}/content`);
  };

  const handleTakeQuiz = () => {
    navigate(`/courses/${courseId}/quiz`);
  };

  const handleYouTubeLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleSectionClick = (sectionId: string) => {
    // Find section index
    const sections = selectedCourse.sections || [];
    const sortedSections = [...sections].sort((a, b) => a.order - b.order);
    const sectionIndex = sortedSections.findIndex((s) => s.id === sectionId);
    if (sectionIndex !== -1) {
      navigate(`/courses/${courseId}/content?section=${sectionIndex}`);
    }
  };

  const siteUrl = 'https://learn2skill.com';
  const courseUrl = `${siteUrl}/courses/${selectedCourse.id}`;
  const courseImage = `${siteUrl}/og-image.jpg`;

  const courseStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: selectedCourse.title,
    description: selectedCourse.description,
    provider: {
      '@type': 'Organization',
      name: 'Learn2Skill',
      url: siteUrl,
    },
    courseCode: selectedCourse.id,
    educationalLevel: 'Beginner to Advanced',
    teaches: selectedCourse.category,
    inLanguage: 'en',
    isAccessibleForFree: true,
    aggregateRating: selectedCourse.averageRating
      ? {
          '@type': 'AggregateRating',
          ratingValue: selectedCourse.averageRating.toString(),
          reviewCount: selectedCourse.ratingCount?.toString() || '0',
        }
      : undefined,
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      instructor: {
        '@type': 'Person',
        name: 'Learn2Skill Expert Team',
      },
    },
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Courses',
        item: `${siteUrl}/courses`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: selectedCourse.title,
        item: courseUrl,
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${selectedCourse.title} - Free Online Course | Learn2Skill`}
        description={selectedCourse.description}
        keywords={`${selectedCourse.title}, ${selectedCourse.category}, programming course, coding tutorial, learn ${selectedCourse.category}, online course, free course`}
        image={courseImage}
        url={`/courses/${selectedCourse.id}`}
        type="course"
        canonicalUrl={courseUrl}
        structuredData={[courseStructuredData, breadcrumbStructuredData]}
      />
      <Box>
        {/* Header Section */}
        <Box
        sx={{
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          py: 6,
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: 'radial-gradient(circle at 50% 0%, rgba(99, 102, 241, 0.15) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={600}>
            <Box>
              <Button
                onClick={() => navigate('/courses')}
                startIcon={<ArrowBackIcon />}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateX(-4px)',
                  },
                }}
              >
                Back to Courses
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {selectedCourse.title}
                  </Typography>
                  <Chip
                    label={selectedCourse.category}
                    sx={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      px: 1,
                    }}
                  />
                </Box>
              </Box>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  maxWidth: '800px',
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                }}
              >
                {selectedCourse.description}
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Main Content with AdSense Banners */}
      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Grid container spacing={3}>
          {/* Left AdSense Banner */}
          <Grid item xs={0} lg={2} sx={{ display: { xs: 'none', lg: 'block' } }}>
            <AdSenseBanner
              slot="1234567890"
              format="vertical"
              position="left"
            />
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} lg={8}>
            {selectedCourse.sections && selectedCourse.sections.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <BookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Course Structure
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  {[...selectedCourse.sections]
                    .sort((a, b) => a.order - b.order)
                    .map((section, index) => (
                      <Grid item xs={12} md={6} key={section.id}>
                        <Grow in timeout={800 + index * 100}>
                          <Card
                            onClick={() => handleSectionClick(section.id)}
                            sx={{
                              height: '100%',
                              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                              border: '1px solid rgba(148, 163, 184, 0.1)',
                              borderRadius: 3,
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                borderColor: 'rgba(99, 102, 241, 0.3)',
                                boxShadow: '0 12px 24px rgba(99, 102, 241, 0.15)',
                              },
                            }}
                          >
                            <CardContent sx={{ p: 3 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  mb: 2,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                    color: 'primary.main',
                                  }}
                                >
                                  {section.order}
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                  {section.title}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  maxHeight: '150px',
                                  overflow: 'hidden',
                                  position: 'relative',
                                  '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: '40px',
                                    background: 'linear-gradient(to bottom, transparent, rgba(30, 41, 59, 0.95))',
                                  },
                                }}
                              >
                                <MarkdownRenderer content={section.content.substring(0, 300) + '...'} />
                              </Box>
                            </CardContent>
                          </Card>
                        </Grow>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            )}

            {selectedCourse.youtubeLinks && selectedCourse.youtubeLinks.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <YouTubeIcon sx={{ fontSize: 32, color: 'error.main' }} />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    YouTube Reference Links
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {selectedCourse.youtubeLinks.map((link, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Grow in timeout={1000 + index * 100}>
                        <Card
                          sx={{
                            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                            border: '1px solid rgba(148, 163, 184, 0.1)',
                            borderRadius: 3,
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              borderColor: 'rgba(239, 68, 68, 0.3)',
                              boxShadow: '0 12px 24px rgba(239, 68, 68, 0.15)',
                            },
                          }}
                          onClick={() => handleYouTubeLink(link.url)}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                              <YouTubeIcon sx={{ fontSize: 32, color: 'error.main', flexShrink: 0 }} />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: 'primary.light',
                                  }}
                                >
                                  {link.title || link.url}
                                </Typography>
                                {link.description && (
                                  <Typography variant="body2" color="text.secondary">
                                    {link.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grow>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            <Fade in timeout={1200}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  mb: 6,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStartCourse}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(99, 102, 241, 0.4)',
                    },
                  }}
                >
                  Start Course
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<QuizIcon />}
                  onClick={handleTakeQuiz}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    borderColor: 'rgba(148, 163, 184, 0.3)',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      background: 'rgba(99, 102, 241, 0.1)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  Take Quiz
                </Button>
              </Box>
            </Fade>

            {/* Ratings Section */}
            <Fade in timeout={1400}>
              <Box sx={{ mb: 6 }}>
                <CourseRating course={selectedCourse} />
              </Box>
            </Fade>

            {/* Comments Section */}
            <Fade in timeout={1600}>
              <Box>
                <CourseComments course={selectedCourse} />
              </Box>
            </Fade>
          </Grid>

          {/* Right AdSense Banner */}
          <Grid item xs={0} lg={2} sx={{ display: { xs: 'none', lg: 'block' } }}>
            <AdSenseBanner
              slot="0987654321"
              format="vertical"
              position="right"
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
    </>
  );
};

export default CourseDetailPage;

